import { useLocation } from "wouter"
import { KioskListLayout } from "../components/KioskListLayout"
import { ParticipantButton } from "../components/ParticipantButton"
import { fetchSession } from "../api"
import { paths, pathHelpers } from "../../../paths"
import { useKioskIdleTimeout } from "../hooks/useKioskIdleTimeout"
import { useKioskQuery } from "../hooks/useKioskQuery"
import { useCreateInvitation } from "../hooks/useCreateInvitation"
import styles from "./SessionParticipantsPage.module.scss"

type Props = {
  params: { sessionId: string }
}

export function SessionParticipantsPage({ params }: Props) {
  const [, navigate] = useLocation()
  useKioskIdleTimeout()

  // A 4xx is the session having aged out or been fully answered — an answer,
  // not a fault — so only a server failure is reported, by the query client.
  // Either way the client goes back to the list.
  const { data: session, loading } = useKioskQuery({
    queryKey: ["kiosk-session", params.sessionId],
    queryFn: () => fetchSession(params.sessionId),
    onError: () => navigate(paths.kioskSessions),
    meta: { source: "fetchSession", clientErrorsAreExpected: true },
  })

  const { create, error } = useCreateInvitation((invitationId) =>
    navigate(pathHelpers.toKioskFeedback(invitationId)),
  )

  return (
    <KioskListLayout
      heading="We've identified that this session had more than one participant"
      subtitle="Please select the one based on your name initials."
      back={{ href: paths.kioskWelcome, label: "Return to home screen" }}
    >
      {loading && (
        <div
          role="status"
          aria-label="Loading participants"
          className={styles.skeletonList}
        >
          {[0, 1].map((i) => (
            <span key={i} className={styles.skeletonRow} />
          ))}
        </div>
      )}
      {session?.contacts.map((c) => (
        <ParticipantButton
          key={c.id}
          firstName={c.firstName}
          onClick={async () => {
            await create(session.id, c.id)
          }}
        />
      ))}
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
    </KioskListLayout>
  )
}
